import {
  Phone,
  Mail,
  MessageSquare,
  Calendar,
  Check,
  Circle,
} from "lucide-react";
import { formatDateShort, formatDateTime, todayISO, isOverdue, isDueToday } from "@/lib/crmHelpers";

const TYPE_ICON = {
  appel: Phone,
  email: Mail,
  sms: MessageSquare,
  rdv: Calendar,
};

const TYPE_COLOR = {
  appel: "text-blue-600 dark:text-blue-400",
  email: "text-violet-600 dark:text-violet-400",
  sms: "text-emerald-600 dark:text-emerald-400",
  rdv: "text-amber-600 dark:text-amber-400",
};

export default function Programme20j({ programme = [], onToggle, readOnly = false }) {
  const done = programme.filter((p) => p.fait).length;
  const pct = programme.length ? Math.round((done / programme.length) * 100) : 0;

  return (
    <div data-testid="programme-20j" className="space-y-3">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-label text-muted-foreground">Programme 20 jours</div>
          <p className="mt-0.5 text-xs text-muted-foreground">
            Plan de conversion automatique pour ce lead
          </p>
        </div>
        <div className="text-right">
          <div className="font-heading text-lg font-semibold leading-none">{pct}%</div>
          <div className="mt-0.5 text-[11px] text-muted-foreground">
            {done} / {programme.length} étapes
          </div>
        </div>
      </div>

      <div className="h-1 w-full overflow-hidden rounded-full bg-muted">
        <div
          className="h-full rounded-full bg-primary transition-all duration-300"
          style={{ width: `${pct}%` }}
        />
      </div>

      <ol className="relative space-y-2 border-l border-border/60 pl-0">
        {programme.map((p, i) => {
          const Icon = TYPE_ICON[p.type] || Circle;
          const overdue = !p.fait && isOverdue(p.date_cible);
          const dueToday = !p.fait && isDueToday(p.date_cible);
          return (
            <li
              key={`${p.jour}-${i}`}
              className={`relative flex items-start gap-3 rounded-lg border px-3 py-2.5 transition-colors ${
                p.fait
                  ? "border-emerald-200 bg-emerald-50/50 dark:border-emerald-900/40 dark:bg-emerald-950/20"
                  : overdue
                  ? "border-red-200 bg-red-50/40 dark:border-red-900/40 dark:bg-red-950/20"
                  : dueToday
                  ? "border-amber-300 bg-amber-50/50 dark:border-amber-900/40 dark:bg-amber-950/20"
                  : "border-border/60 bg-card"
              }`}
            >
              <button
                type="button"
                disabled={readOnly}
                onClick={() => onToggle?.(p.jour, i, !p.fait)}
                data-testid={`programme-step-${p.jour}-${i}`}
                className={`mt-0.5 flex h-5 w-5 flex-none items-center justify-center rounded border transition-colors ${
                  p.fait
                    ? "border-emerald-500 bg-emerald-500 text-white"
                    : "border-border bg-background hover:border-foreground/40"
                } ${readOnly ? "cursor-default" : "cursor-pointer"}`}
                aria-label={p.fait ? "Marquer comme non fait" : "Marquer comme fait"}
              >
                {p.fait && <Check className="h-3 w-3" strokeWidth={3} />}
              </button>

              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="font-heading text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
                    J{p.jour}
                  </span>
                  <Icon className={`h-3.5 w-3.5 ${TYPE_COLOR[p.type] || "text-muted-foreground"}`} strokeWidth={2} />
                  <span
                    className={`truncate text-sm ${
                      p.fait ? "text-muted-foreground line-through" : "font-medium"
                    }`}
                  >
                    {p.titre}
                  </span>
                </div>
                <div className="mt-0.5 text-[11px] text-muted-foreground">
                  {p.fait ? (
                    <>Fait le {formatDateTime(p.date_fait)}</>
                  ) : (
                    <>
                      Cible : {formatDateShort(p.date_cible)}
                      {overdue && <span className="ml-1 text-red-600 dark:text-red-400">· En retard</span>}
                      {dueToday && <span className="ml-1 text-amber-600 dark:text-amber-400">· Aujourd'hui</span>}
                    </>
                  )}
                </div>
              </div>
            </li>
          );
        })}
      </ol>
    </div>
  );
}
